import React, { useState, useEffect, useRef } from "react";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaPlus,
  FaTimes,
  FaImage,
  FaTools,
} from "react-icons/fa";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import useAxios from "../../api/useAxios";
import Swal from "sweetalert2";

const Products = () => {
  const axiosInstance = useAxios();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    minQuantity: "",
    maxQuantity: "",
    price: "",
    stock: "",
    discount: "",
    isTool: false,
    colorCode: "",
    image: null,
    imageFile: null,
    categoryId: "",
    subCategoryId: "",
    active: true,
  });
  const [viewProduct, setViewProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [filters, setFilters] = useState({
    category: "all",
    subCategory: "all",
    status: "all",
    toolType: "all",
  });
  const fileInputRef = useRef(null);

  // Fetch Products
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get("/products");

      if (Array.isArray(res.data)) {
        setProducts(res.data);
      } else if (res.data && Array.isArray(res.data.data)) {
        setProducts(res.data.data);
      } else if (
        res.data &&
        res.data.content &&
        Array.isArray(res.data.content)
      ) {
        setProducts(res.data.content);
      } else {
        setProducts([]);
        console.error("Unexpected API response structure:", res.data);
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Products loaded successfully",
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: "top-right",
      });
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load products",
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get("/categories");
      setCategories(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // Fetch SubCategories
  const fetchSubCategories = async () => {
    try {
      const res = await axiosInstance.get("/subcategories");
      setSubCategories(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  };

  // Add this function near your other fetch functions (after fetchCategories)
  const getSubCategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    return subCategories.filter((sc) => sc.categoryId === parseInt(categoryId));
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSubCategories();
  }, []);

  // Get product image URL
  const getProductImage = (product) => {
    if (product.imageUrl) {
      return `https://api.adanapaints.com/uploads/brands/${product.imageUrl}`;
    }
    return "https://api.adanapaints.com";
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  // Get subcategory name by ID
  const getSubCategoryName = (subCategoryId) => {
    const subCategory = subCategories.find((sc) => sc.id === subCategoryId);
    return subCategory ? subCategory.name : "Unknown Sub-Category";
  };

  // Sorting function
  const sortedProducts = [...products].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Filter products
  const filteredProducts = sortedProducts.filter((product) => {
    // Text search filter
    const searchMatch =
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      (product.description &&
        product.description.toLowerCase().includes(search.toLowerCase()));

    // Category filter
    const categoryMatch =
      filters.category === "all" ||
      product.categoryId === parseInt(filters.category);

    // SubCategory filter
    const subCategoryMatch =
      filters.subCategory === "all" ||
      product.subCategoryId === parseInt(filters.subCategory);

    // Status filter
    const statusMatch =
      filters.status === "all" ||
      (filters.status === "active" && product.active) ||
      (filters.status === "inactive" && !product.active);

    // Tool filter
    const toolMatch =
      filters.toolType === "all" ||
      (filters.toolType === "tool" && product.isTool) ||
      (filters.toolType === "non-tool" && !product.isTool);

    return (
      searchMatch &&
      categoryMatch &&
      subCategoryMatch &&
      statusMatch &&
      toolMatch
    );
  });

  // Pagination
  const totalEntries = filteredProducts.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalEntries);
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Handle Sort
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (price, discount) => {
    if (!price || !discount) return price || 0;
    return price - (price * discount) / 100;
  };

  // Handle image upload with SweetAlert
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "File Too Large",
          text: "Image size should be less than 5MB",
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }

      if (!file.type.match("image.*")) {
        Swal.fire({
          icon: "warning",
          title: "Invalid File",
          text: "Please select a valid image file (JPG, PNG, etc.)",
          timer: 3000,
          showConfirmButton: false,
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProduct({
          ...newProduct,
          image: reader.result,
          imageFile: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Format color code to hex
  const formatColorCode = (colorCode) => {
    if (!colorCode) return "#000000";
    // If it's already a hex string, return it
    if (typeof colorCode === "string" && colorCode.startsWith("#")) {
      return colorCode;
    }
    // If it's a number, convert to hex
    const hex = parseInt(colorCode).toString(16).padStart(6, "0");
    return `#${hex}`;
  };

  // Parse hex to decimal
  const parseColorCode = (hexColor) => {
    if (!hexColor || !hexColor.startsWith("#")) return "0";
    const hex = hexColor.replace("#", "");
    return parseInt(hex, 16);
  };

  // Validate product form
  const validateProductForm = () => {
    if (!newProduct.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Product name is required",
        timer: 3000,
      });
      return false;
    }

    if (!newProduct.subCategoryId) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select a sub-category",
        timer: 3000,
      });
      return false;
    }

    // Only validate color code if it's a tool
    if (newProduct.isTool) {
      if (!newProduct.colorCode || !newProduct.colorCode.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Color code is required for tools",
          timer: 3000,
        });
        return false;
      }
      // Validate hex color format
      const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (!hexColorRegex.test(newProduct.colorCode)) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please enter a valid hex color code (e.g., #FF0000)",
          timer: 3000,
        });
        return false;
      }
    }

    return true;
  };

  // Handle Save (Create/Update) with SweetAlert
  const handleSaveProduct = async () => {
    if (!validateProductForm()) return;

    const formData = new FormData();
    formData.append("name", newProduct.name.trim());
    formData.append("description", newProduct.description?.trim() || "");
    formData.append("minQuantity", newProduct.minQuantity || "1");
    formData.append("maxQuantity", newProduct.maxQuantity || "100");
    formData.append("price", newProduct.price || "0");
    formData.append("stock", newProduct.stock || "0");
    formData.append("discount", newProduct.discount || "0");
    formData.append("isTool", newProduct.isTool.toString());

    // Convert hex color to decimal for API
    if (newProduct.isTool && newProduct.colorCode) {
      const decimalColor = parseColorCode(newProduct.colorCode);
      formData.append("colorCode", decimalColor.toString());
    }

    formData.append("categoryId", newProduct.categoryId || "");
    formData.append("subCategoryId", newProduct.subCategoryId);
    formData.append("active", newProduct.active.toString());

    // Only append image if a new file was selected
    if (newProduct.imageFile) {
      formData.append("image", newProduct.imageFile);
    }

    setIsLoading(true);
    try {
      if (editProductId) {
        await axiosInstance.put(`/products/${editProductId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Product updated successfully",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-right",
        });
      } else {
        await axiosInstance.post(`/products`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Product created successfully",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-right",
        });
      }

      setShowModal(false);
      setEditProductId(null);
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error("Error saving product:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to save product",
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Delete with SweetAlert Confirmation
  const handleDeleteProduct = async (id, productName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete "${productName}". This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/products/${id}`);

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Product has been deleted successfully.",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-right",
        });

        fetchProducts();
      } catch (err) {
        console.error("Error deleting product:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.message || "Failed to delete product",
          timer: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Toggle Status with SweetAlert Confirmation
  const handleToggleStatus = async (product) => {
    const newStatus = !product.active;
    const statusText = newStatus ? "activate" : "deactivate";

    const result = await Swal.fire({
      title: "Change Status",
      text: `Are you sure you want to ${statusText} "${product.name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${statusText}`,
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.patch(`/products/${product.id}/status`, null, {
          params: { active: newStatus },
        });

        Swal.fire({
          icon: "success",
          title: "Status Updated!",
          text: `Product has been ${newStatus ? "activated" : "deactivated"}`,
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-right",
        });

        // Update local state immediately
        setProducts((prevProducts) =>
          prevProducts.map((p) =>
            p.id === product.id ? { ...p, active: newStatus } : p
          )
        );
      } catch (err) {
        console.error("Error toggling status:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.response?.data?.message || "Failed to update status",
          timer: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setNewProduct({
      name: "",
      description: "",
      minQuantity: "",
      maxQuantity: "",
      price: "",
      stock: "",
      discount: "",
      isTool: false,
      colorCode: "",
      image: null,
      imageFile: null,
      categoryId: "",
      subCategoryId: "",
      active: true,
    });
    setEditProductId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open edit modal
  const openEditModal = async (product) => {
    try {
      // Fetch fresh product data
      const res = await axiosInstance.get(`/products/${product.id}`);
      const freshProduct = res.data?.data || product;

      setNewProduct({
        name: freshProduct.name,
        description: freshProduct.description || "",
        minQuantity: freshProduct.minQuantity?.toString() || "",
        maxQuantity: freshProduct.maxQuantity?.toString() || "",
        price: freshProduct.price?.toString() || "",
        stock: freshProduct.stock?.toString() || "",
        discount: freshProduct.discount?.toString() || "",
        isTool: freshProduct.isTool || false,
        colorCode: freshProduct.isTool
          ? formatColorCode(freshProduct.colorCode)
          : "",
        image: getProductImage(freshProduct),
        imageFile: null,
        categoryId: freshProduct.categoryId?.toString() || "",
        subCategoryId: freshProduct.subCategoryId?.toString() || "",
        active: freshProduct.active,
      });
      setEditProductId(freshProduct.id);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching product details:", err);
      // Use cached data if fetch fails
      setNewProduct({
        name: product.name,
        description: product.description || "",
        minQuantity: product.minQuantity?.toString() || "",
        maxQuantity: product.maxQuantity?.toString() || "",
        price: product.price?.toString() || "",
        stock: product.stock?.toString() || "",
        discount: product.discount?.toString() || "",
        isTool: product.isTool || false,
        colorCode: product.isTool ? formatColorCode(product.colorCode) : "",
        image: getProductImage(product),
        imageFile: null,
        categoryId: product.categoryId?.toString() || "",
        subCategoryId: product.subCategoryId?.toString() || "",
        active: product.active,
      });
      setEditProductId(product.id);
      setShowModal(true);
    }
  };

  // Reset filters with SweetAlert
  const resetFilters = () => {
    setFilters({
      category: "all",
      subCategory: "all",
      status: "all",
      toolType: "all",
    });
    setCurrentPage(1);

    Swal.fire({
      icon: "success",
      title: "Filters Reset",
      text: "All filters have been reset to default",
      timer: 2000,
      showConfirmButton: false,
      toast: true,
      position: "top-right",
    });
  };

  // Get unique subcategories for selected category
  const getFilteredSubCategories = () => {
    if (filters.category === "all") return subCategories;
    return subCategories.filter(
      (sc) => sc.categoryId === parseInt(filters.category)
    );
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle view modal
  const openViewModal = async (product) => {
    try {
      setIsLoading(true);
      // Fetch fresh data for view
      const res = await axiosInstance.get(`/products/${product.id}`);
      setViewProduct(res.data?.data || product);
      setShowViewModal(true);
    } catch (err) {
      setViewProduct(product);
      setShowViewModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Get active status of save button
  const getSaveButtonDisabled = () => {
    return isLoading || !newProduct.name.trim() || !newProduct.subCategoryId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* HEADER */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Product Management
        </h1>
        <p className="text-gray-600">
          Manage your product inventory, pricing, and details
        </p>
      </div>

      {/* FILTER SECTION */}
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border border-gray-200">
        <h2 className="font-semibold text-gray-800 mb-4">Filter Products</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* CATEGORY FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  category: e.target.value,
                  subCategory: "all",
                });
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              disabled={isLoading}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* SUB CATEGORY FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sub-Category
            </label>
            <select
              value={filters.subCategory}
              onChange={(e) => {
                setFilters({ ...filters, subCategory: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              disabled={isLoading}
            >
              <option value="all">All Sub-Categories</option>
              {getFilteredSubCategories().map((subCategory) => (
                <option key={subCategory.id} value={subCategory.id}>
                  {subCategory.name}
                </option>
              ))}
            </select>
          </div>

          {/* STATUS FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              disabled={isLoading}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* TOOL FILTER */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Type
            </label>
            <select
              value={filters.toolType}
              onChange={(e) => {
                setFilters({ ...filters, toolType: e.target.value });
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              disabled={isLoading}
            >
              <option value="all">All Types</option>
              <option value="tool">Tools Only</option>
              <option value="non-tool">Non-Tools Only</option>
            </select>
          </div>
        </div>

        {/* FILTER BUTTONS */}
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={resetFilters}
            disabled={isLoading}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* STATUS SUMMARY */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Total Products</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.length}
          </div>
          <div className="text-xs text-gray-400 mt-1">All products</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-500">Active Products</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.filter((p) => p.active).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {products.length > 0
              ? `${Math.round(
                  (products.filter((p) => p.active).length / products.length) *
                    100
                )}% of total`
              : "0%"}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Inactive Products</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.filter((p) => !p.active).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {products.length > 0
              ? `${Math.round(
                  (products.filter((p) => !p.active).length / products.length) *
                    100
                )}% of total`
              : "0%"}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-500">
          <div className="text-sm text-gray-500">Low Stock</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.filter((p) => p.stock < 20 && p.stock > 0).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">Stock below 20</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-500">
          <div className="text-sm text-gray-500">Out of Stock</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.filter((p) => p.stock <= 0).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">No stock available</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-orange-500">
          <div className="text-sm text-gray-500">Tools</div>
          <div className="text-2xl font-bold text-gray-800">
            {products.filter((p) => p.isTool).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {products.length > 0
              ? `${Math.round(
                  (products.filter((p) => p.isTool).length / products.length) *
                    100
                )}% of total`
              : "0%"}
          </div>
        </div>
      </div>

      {/* CONTROLS CARD */}
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
            {/* SEARCH */}
            <div className="relative flex-1 max-w-lg">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by name or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                disabled={isLoading}
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* ROWS PER PAGE */}
            <div className="flex items-center gap-3">
              <span className="text-gray-600 whitespace-nowrap">Show</span>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > 0) {
                      setRowsPerPage(value);
                      setCurrentPage(1);
                    }
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10"
                  disabled={isLoading}
                >
                  {[5, 10, 20, 50].map((num) => (
                    <option key={num} value={num}>
                      {num} rows
                    </option>
                  ))}
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ADD BUTTON */}
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap w-full md:w-auto justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <FaPlus />
                <span>Add New Product</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* MAIN TABLE CARD */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        {/* TABLE HEADER */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Product Details
              </h2>
              <div className="text-sm text-gray-600 mt-1">
                Showing {filteredProducts.length} products
                {filters.status !== "all" && ` (${filters.status} only)`}
                {filters.toolType !== "all" && ` (${filters.toolType} only)`}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {totalEntries > 0 && `Total: ${totalEntries} products`}
            </div>
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading products...</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      #
                    </th>
                    <th
                      className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-2">
                        Product Name
                        {sortConfig.key === "name" &&
                          (sortConfig.direction === "asc" ? (
                            <IoMdArrowDropup />
                          ) : (
                            <IoMdArrowDropdown />
                          ))}
                      </div>
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Image
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Category
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Stock
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Price
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Type
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="px-4 md:px-6 py-4 text-center font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentProducts.map((product, index) => (
                    <tr
                      key={product.id}
                      className={`border-b border-gray-100 hover:bg-blue-50 transition-colors group ${
                        !product.active
                          ? "bg-red-50 hover:bg-red-100"
                          : product.stock < 20
                          ? "bg-yellow-50 hover:bg-yellow-100"
                          : ""
                      }`}
                    >
                      <td className="px-4 md:px-6 py-4">
                        <div className="text-gray-600 font-medium">
                          {startIndex + index + 1}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div>
                          <div className="font-semibold text-gray-900 group-hover:text-blue-700">
                            {product.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {product.id}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          className="h-12 w-12 rounded-lg object-cover border border-gray-200 shadow-sm"
                        />
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-700">
                            {product.subCategoryName ||
                              getSubCategoryName(product.subCategoryId)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {product.categoryName ||
                              getCategoryName(product.categoryId)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div>
                          <div
                            className={`font-medium ${
                              product.stock <= 0
                                ? "text-red-600"
                                : product.stock < 20
                                ? "text-yellow-600"
                                : "text-gray-900"
                            }`}
                          >
                            {product.stock} units
                          </div>
                          {product.stock < 20 && product.stock > 0 && (
                            <div className="text-xs text-yellow-600 font-medium">
                              Low stock
                            </div>
                          )}
                          {product.stock <= 0 && (
                            <div className="text-xs text-red-600 font-medium">
                              Out of stock
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            ₹
                            {calculateDiscountedPrice(
                              product.price,
                              product.discount
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            Base: ₹{product.price || "0.00"}
                            {product.discount > 0 && (
                              <span className="text-green-600 ml-1">
                                ({product.discount}% off)
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex items-center gap-2">
                          {product.isTool ? (
                            <>
                              <FaTools className="text-blue-600" size={14} />
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                Tool
                              </span>
                              {product.colorCode && (
                                <div
                                  className="w-4 h-4 rounded-full border border-gray-300"
                                  style={{
                                    backgroundColor: formatColorCode(
                                      product.colorCode
                                    ),
                                  }}
                                  title={`Color: ${formatColorCode(
                                    product.colorCode
                                  )}`}
                                />
                              )}
                            </>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                              Product
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleStatus(product)}
                            disabled={isLoading}
                            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              product.active
                                ? "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                                : "bg-gray-300 hover:bg-gray-400 focus:ring-gray-400"
                            } ${
                              isLoading ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                          >
                            <span
                              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                                product.active
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              product.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {product.active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => openViewModal(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors hover:scale-110"
                            title="View Details"
                            disabled={isLoading}
                          >
                            <FaEye size={18} />
                          </button>
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors hover:scale-110"
                            title="Edit Product"
                            disabled={isLoading}
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteProduct(product.id, product.name)
                            }
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors hover:scale-110"
                            title="Delete Product"
                            disabled={isLoading}
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* EMPTY STATE */}
              {!isLoading && currentProducts.length === 0 && (
                <div className="py-16 text-center">
                  <div className="text-gray-400 mb-4">
                    <FaSearch size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    No products found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {search || Object.values(filters).some((f) => f !== "all")
                      ? "Try adjusting your search or filter terms"
                      : "Add your first product to get started"}
                  </p>
                  {!search &&
                    Object.values(filters).every((f) => f === "all") && (
                      <button
                        onClick={() => {
                          resetForm();
                          setShowModal(true);
                        }}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Create Your First Product
                      </button>
                    )}
                </div>
              )}
            </>
          )}
        </div>

        {/* PAGINATION */}
        {totalEntries > 0 && totalPages > 1 && (
          <div className="px-4 md:px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{startIndex + 1}</span>{" "}
                to <span className="font-semibold">{endIndex}</span> of{" "}
                <span className="font-semibold">{totalEntries}</span> products
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }).map(
                    (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={i}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 min-w-[40px] rounded transition-colors ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white shadow-sm"
                              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  Next
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {editProductId ? "Edit Product" : "Add New Product"}
                  </h3>
                  {editProductId && (
                    <p className="text-sm text-gray-600 mt-1">
                      ID: {editProductId}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isLoading}
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* MODAL BODY */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT COLUMN */}
                <div className="space-y-6">
                  {/* PRODUCT NAME */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter product name"
                      value={newProduct.name}
                      onChange={(e) =>
                        setNewProduct({ ...newProduct, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      disabled={isLoading}
                    />
                  </div>

                  {/* DESCRIPTION */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      placeholder="Enter product description (optional)"
                      value={newProduct.description}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          description: e.target.value,
                        })
                      }
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                      disabled={isLoading}
                    />
                  </div>

                  {/* CATEGORY SELECTION */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={newProduct.categoryId}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            categoryId: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={isLoading || categories.length === 0}
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      {categories.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          No categories available. Please add categories first.
                        </p>
                      )}
                    </div>

                    {/* SUB-CATEGORY SELECTION */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sub-Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newProduct.subCategoryId}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            subCategoryId: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={
                          isLoading ||
                          getSubCategoriesForCategory(newProduct.categoryId)
                            .length === 0
                        }
                      >
                        <option value="">Select a sub-category</option>
                        {getSubCategoriesForCategory(newProduct.categoryId).map(
                          (subCategory) => (
                            <option key={subCategory.id} value={subCategory.id}>
                              {subCategory.name}
                            </option>
                          )
                        )}
                      </select>
                      {subCategories.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          No sub-categories available. Please add sub-categories
                          first.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* PRICE & STOCK */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (₹)
                      </label>
                      <input
                        type="number"
                        placeholder="Enter price"
                        value={newProduct.price}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            price: e.target.value,
                          })
                        }
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stock Quantity
                      </label>
                      <input
                        type="number"
                        placeholder="Enter stock quantity"
                        value={newProduct.stock}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            stock: e.target.value,
                          })
                        }
                        min="0"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* MIN/MAX QUANTITY */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Min Quantity
                      </label>
                      <input
                        type="number"
                        placeholder="Min"
                        value={newProduct.minQuantity}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            minQuantity: e.target.value,
                          })
                        }
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Quantity
                      </label>
                      <input
                        type="number"
                        placeholder="Max"
                        value={newProduct.maxQuantity}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            maxQuantity: e.target.value,
                          })
                        }
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* DISCOUNT */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      placeholder="Discount"
                      value={newProduct.discount}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          discount: e.target.value,
                        })
                      }
                      min="0"
                      max="100"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-6">
                  {/* IMAGE UPLOAD */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Image
                      {editProductId && (
                        <span className="text-xs text-gray-500 ml-2">
                          (Upload new image only if you want to change it)
                        </span>
                      )}
                    </label>
                    <div className="space-y-4">
                      {/* Preview */}
                      {(newProduct.image ||
                        (editProductId && !newProduct.imageFile)) && (
                        <div className="flex flex-col items-center">
                          <img
                            src={newProduct.image}
                            alt="Preview"
                            className="h-48 w-48 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                          />
                          <div className="mt-2 text-xs text-gray-500 text-center">
                            {editProductId && !newProduct.imageFile
                              ? "Current image - upload new to replace"
                              : "New image preview"}
                          </div>
                        </div>
                      )}

                      {/* Upload Button */}
                      <label className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer">
                        <FaImage className="text-gray-400 mb-2" size={32} />
                        <span className="text-sm text-gray-600">
                          {newProduct.imageFile
                            ? "Change Image"
                            : editProductId
                            ? "Upload New Image (Optional)"
                            : "Click to upload image"}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          PNG, JPG up to 5MB
                        </span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          disabled={isLoading}
                        />
                      </label>
                    </div>
                  </div>

                  {/* TOOL SELECTION & COLOR CODE */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        id="isTool"
                        checked={newProduct.isTool}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            isTool: e.target.checked,
                            // Clear color code when unchecking tool
                            colorCode: e.target.checked
                              ? newProduct.colorCode
                              : "",
                          })
                        }
                        className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <label
                        htmlFor="isTool"
                        className="text-sm font-medium text-gray-700 flex items-center gap-2"
                      >
                        <FaTools className="text-gray-500" />
                        This product is a tool
                      </label>
                    </div>

                    {/* COLOR CODE INPUT - Only shown when isTool is true */}
                    {newProduct.isTool && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Color Code <span className="text-red-500">*</span>
                          <span className="text-xs text-gray-500 ml-2">
                            (Hex format, e.g., #FF0000 for red)
                          </span>
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            placeholder="#FF0000"
                            value={newProduct.colorCode}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                colorCode: e.target.value,
                              })
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            disabled={isLoading}
                          />
                          {newProduct.colorCode && (
                            <div
                              className="w-10 h-10 rounded-lg border border-gray-300 shadow-sm"
                              style={{ backgroundColor: newProduct.colorCode }}
                              title="Color preview"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* DISCOUNTED PRICE CALCULATION */}
                  {newProduct.price && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                      <div className="text-sm font-medium text-gray-700 mb-1">
                        Price Calculation
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        ₹
                        {calculateDiscountedPrice(
                          parseFloat(newProduct.price) || 0,
                          parseFloat(newProduct.discount) || 0
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Original: ₹{newProduct.price || "0.00"}
                        {newProduct.discount &&
                          ` • Discount: ${newProduct.discount}%`}
                      </div>
                    </div>
                  )}

                  {/* STATUS */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={newProduct.active}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          active: e.target.value === "true",
                        })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      disabled={isLoading}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sticky bottom-0">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProduct}
                  disabled={getSaveButtonDisabled()}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[120px] justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : editProductId ? (
                    "Update Product"
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && viewProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Product Details
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    ID: {viewProduct.id}
                  </p>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* LEFT COLUMN */}
                <div className="space-y-6">
                  {/* IMAGE */}
                  <div className="flex justify-center">
                    <img
                      src={getProductImage(viewProduct)}
                      alt={viewProduct.name}
                      className="h-64 w-64 rounded-xl object-cover border-4 border-white shadow-2xl"
                    />
                  </div>

                  {/* BASIC INFO */}
                  <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                        Product ID
                      </label>
                      <p className="text-lg font-bold text-gray-900">
                        {viewProduct.id}
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                        Name
                      </label>
                      <p className="text-xl font-bold text-gray-900">
                        {viewProduct.name}
                      </p>
                    </div>

                    {viewProduct.description && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                          Description
                        </label>
                        <p className="text-gray-700 leading-relaxed">
                          {viewProduct.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-6">
                  {/* CATEGORY INFO */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      Category Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                          Category
                        </label>
                        <p className="text-lg font-medium text-blue-600">
                          {viewProduct.categoryName ||
                            getCategoryName(viewProduct.categoryId)}
                        </p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                          Sub-Category
                        </label>
                        <p className="text-lg font-medium text-green-600">
                          {viewProduct.subCategoryName ||
                            getSubCategoryName(viewProduct.subCategoryId)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* PRICING INFO */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      Pricing Information
                    </h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                          Base Price
                        </label>
                        <p className="text-2xl font-bold text-gray-900">
                          ₹{viewProduct.price || "0.00"}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                          Discount
                        </label>
                        <p className="text-2xl font-bold text-blue-600">
                          {viewProduct.discount || "0"}%
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                        Final Price After Discount
                      </label>
                      <p className="text-3xl font-bold text-green-600">
                        ₹
                        {calculateDiscountedPrice(
                          viewProduct.price,
                          viewProduct.discount
                        )}
                      </p>
                    </div>
                  </div>

                  {/* INVENTORY INFO */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">
                      Inventory Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div
                        className={`p-3 rounded-lg ${
                          viewProduct.stock <= 0
                            ? "bg-red-50"
                            : viewProduct.stock < 20
                            ? "bg-yellow-50"
                            : "bg-green-50"
                        }`}
                      >
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                          Current Stock
                        </label>
                        <p
                          className={`text-2xl font-bold ${
                            viewProduct.stock <= 0
                              ? "text-red-600"
                              : viewProduct.stock < 20
                              ? "text-yellow-600"
                              : "text-green-600"
                          }`}
                        >
                          {viewProduct.stock} units
                        </p>
                        <span
                          className={`text-xs font-medium ${
                            viewProduct.stock <= 0
                              ? "text-red-600"
                              : viewProduct.stock < 20
                              ? "text-yellow-600"
                              : "text-green-600"
                          }`}
                        >
                          {viewProduct.stock <= 0
                            ? "Out of Stock"
                            : viewProduct.stock < 20
                            ? "Low Stock"
                            : "In Stock"}
                        </span>
                      </div>

                      <div className="p-3 bg-blue-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                          Min Quantity
                        </label>
                        <p className="text-lg font-bold text-blue-600">
                          {viewProduct.minQuantity || "1"}
                        </p>
                      </div>

                      <div className="p-3 bg-purple-50 rounded-lg">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1">
                          Max Quantity
                        </label>
                        <p className="text-lg font-bold text-purple-600">
                          {viewProduct.maxQuantity || "100"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* STATUS & TYPE */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        Product Status
                      </h4>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-4 py-2 rounded-full font-medium ${
                            viewProduct.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {viewProduct.active ? "Active" : "Inactive"}
                        </span>
                        <div className="relative">
                          <div
                            className={`w-12 h-6 rounded-full ${
                              viewProduct.active
                                ? "bg-green-500"
                                : "bg-gray-300"
                            }`}
                          />
                          <div
                            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transform transition-transform ${
                              viewProduct.active
                                ? "translate-x-7"
                                : "translate-x-0.5"
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">
                        Product Type
                      </h4>
                      <div className="flex items-center gap-3">
                        {viewProduct.isTool ? (
                          <>
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <FaTools className="text-blue-600" size={20} />
                            </div>
                            <div>
                              <p className="font-medium text-blue-600">Tool</p>
                              {viewProduct.colorCode && (
                                <div className="flex items-center gap-2 mt-1">
                                  <div
                                    className="w-4 h-4 rounded-full border border-gray-300"
                                    style={{
                                      backgroundColor: formatColorCode(
                                        viewProduct.colorCode
                                      ),
                                    }}
                                  />
                                  <span className="text-sm text-gray-600">
                                    Color:{" "}
                                    {formatColorCode(viewProduct.colorCode)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <p className="text-lg font-medium text-gray-600">
                            Regular Product
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    openEditModal(viewProduct);
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  disabled={isLoading}
                >
                  <FaEdit />
                  Edit Product
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleToggleStatus(viewProduct)}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={isLoading}
                  >
                    Toggle Status
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Products;
